import type {
  Comparison,
  LeaderboardRow,
  MetricAverage,
  MetricDefinition,
  MetricKey,
  PrimaryMetricKey,
  PublicResult,
} from '../types/result';

export const metricDefinitions: Record<MetricKey, MetricDefinition> = {
  sprint40Seconds: {
    key: 'sprint40Seconds',
    label: '40-yard dash',
    shortLabel: '40 yd',
    unit: 's',
    decimals: 3,
    direction: 'lower',
  },
  broadJumpInches: {
    key: 'broadJumpInches',
    label: 'Broad jump',
    shortLabel: 'Broad',
    unit: 'in',
    decimals: 2,
    direction: 'higher',
  },
  verticalJumpInches: {
    key: 'verticalJumpInches',
    label: 'Vertical jump',
    shortLabel: 'Vertical',
    unit: 'in',
    decimals: 2,
    direction: 'higher',
  },
  proAgilitySeconds: {
    key: 'proAgilitySeconds',
    label: 'Pro agility',
    shortLabel: 'Pro Agility',
    unit: 's',
    decimals: 3,
    direction: 'lower',
  },
  threeConeSeconds: {
    key: 'threeConeSeconds',
    label: 'Three-cone drill',
    shortLabel: '3-cone',
    unit: 's',
    decimals: 3,
    direction: 'lower',
  },
  throwingAccuracy: {
    key: 'throwingAccuracy',
    label: 'Throwing accuracy',
    shortLabel: 'Accuracy',
    unit: '%',
    decimals: 0,
    direction: 'higher',
  },
  throwingPower: {
    key: 'throwingPower',
    label: 'Throwing power',
    shortLabel: 'Power',
    unit: 'mph',
    decimals: 0,
    direction: 'higher',
  },
};

export const primaryMetricKeys: PrimaryMetricKey[] = [
  'sprint40Seconds',
  'broadJumpInches',
  'verticalJumpInches',
  'proAgilitySeconds',
];

function compareOverallScore(
  left: Pick<PublicResult, 'externalId' | 'overallScore'>,
  right: Pick<PublicResult, 'externalId' | 'overallScore'>,
): number {
  if (left.overallScore === null && right.overallScore === null) {
    return left.externalId.localeCompare(right.externalId);
  }

  if (left.overallScore === null) {
    return 1;
  }

  if (right.overallScore === null) {
    return -1;
  }

  const scoreDifference = right.overallScore - left.overallScore;
  return scoreDifference !== 0
    ? scoreDifference
    : left.externalId.localeCompare(right.externalId);
}

function assignCompetitionRanks<T extends Pick<PublicResult, 'externalId' | 'overallScore'>>(
  results: readonly T[],
): Array<T & { rank: number }> {
  let previousResult: T | undefined;
  let previousRank = 0;

  return results.map((result, index) => {
    const rank =
      previousResult && previousResult.overallScore === result.overallScore
        ? previousRank
        : index + 1;

    previousResult = result;
    previousRank = rank;

    return {
      ...result,
      rank,
    };
  });
}

export function getDisplayName(result: Pick<PublicResult, 'firstName' | 'lastName'>): string {
  return [result.firstName, result.lastName]
    .filter(Boolean)
    .join(' ')
    .replaceAll('_', ' ');
}

export function getShortAthleteId(externalId: string): string {
  return externalId.split('_')[0] ?? externalId;
}

export function normalizeAthleteId(value: string): string {
  return value.trim().replace(/\s+/g, '_').toLowerCase();
}

export function findParticipantById(
  results: PublicResult[],
  value: string,
): PublicResult | null {
  const normalized = normalizeAthleteId(value);

  return (
    results.find((result) => {
      if (result.isProfessional) {
        return false;
      }

      const fullId = normalizeAthleteId(result.externalId);
      return fullId === normalized || getShortAthleteId(fullId) === normalized;
    }) ?? null
  );
}

export function rankParticipants(results: PublicResult[]): PublicResult[] {
  return results
    .filter((result) => !result.isProfessional)
    .toSorted(compareOverallScore);
}

export function sortResultsByOverallScore(results: PublicResult[]): PublicResult[] {
  return results.toSorted(compareOverallScore);
}

export function toLeaderboardRows(
  results: PublicResult[],
  includeProfessionals = false,
): LeaderboardRow[] {
  const sortedResults = includeProfessionals
    ? sortResultsByOverallScore(results)
    : rankParticipants(results);

  return assignCompetitionRanks(sortedResults).map((result) => ({
    externalId: result.externalId,
    externalIdNoWithoutText: getShortAthleteId(result.externalId),
    firstName: result.firstName,
    lastName: result.lastName,
    isProfessional: result.isProfessional,
    thumbnailImageUrl: result.thumbnailImageUrl,
    rank: result.rank,
    sprint40Seconds: result.sprint40Seconds,
    broadJumpInches: result.broadJumpInches,
    threeConeSeconds: result.threeConeSeconds,
    verticalJumpInches: result.verticalJumpInches,
    proAgilitySeconds: result.proAgilitySeconds,
    throwingAccuracy: result.throwingAccuracy,
    throwingPower: result.throwingPower,
    powerIndex: result.powerIndex,
    overallScore: result.overallScore,
  }));
}

export function searchLeaderboard(rows: LeaderboardRow[], search: string): LeaderboardRow[] {
  const normalized = normalizeAthleteId(search);
  if (!normalized) {
    return rows;
  }

  return rows.filter((row) => {
    const name = normalizeAthleteId(
      [row.firstName, row.lastName].filter(Boolean).join(' '),
    );
    return (
      name.includes(normalized) ||
      normalizeAthleteId(row.externalId).includes(normalized) ||
      getShortAthleteId(row.externalId).includes(normalized)
    );
  });
}

export function averageMetric(results: PublicResult[], key: MetricKey): number | null {
  const values = results
    .map((result) => result[key])
    .filter((value): value is number => typeof value === 'number');

  if (values.length === 0) {
    return null;
  }

  return values.reduce((total, value) => total + value, 0) / values.length;
}

export function buildAverages(results: PublicResult[]): MetricAverage[] {
  return primaryMetricKeys.map((key) => ({
    key,
    value: averageMetric(results, key),
  }));
}

export function buildComparisons(
  athlete: PublicResult,
  benchmarkResults: PublicResult[],
): Comparison[] {
  return primaryMetricKeys.map((key) => {
    const athleteValue = athlete[key];
    const benchmarkValue = averageMetric(benchmarkResults, key);
    const difference =
      athleteValue === null || benchmarkValue === null ? null : athleteValue - benchmarkValue;

    return {
      key,
      athleteValue,
      benchmarkValue,
      difference,
      isFavorable:
        difference === null
          ? null
          : metricDefinitions[key].direction === 'lower'
            ? difference < 0
            : difference > 0,
    };
  });
}

export function formatMetric(value: number | null, key: MetricKey): string {
  if (value === null) {
    return '-';
  }

  const definition = metricDefinitions[key];
  return `${value.toFixed(definition.decimals)} ${definition.unit}`;
}

export function formatOverallScore(value: number | null): string {
  return value === null ? '-' : value.toFixed(2);
}

export function formatHeight(value: number | null): string {
  if (value === null) {
    return '-';
  }

  const feet = Math.floor(value / 12);
  const inches = Math.round((value - feet * 12) * 4) / 4;
  return `${feet}′ ${inches}″`;
}

export function formatComparison(comparison: Comparison): string {
  if (comparison.difference === null || comparison.isFavorable === null) {
    return 'Comparison unavailable';
  }

  const definition = metricDefinitions[comparison.key];
  const amount = Math.abs(comparison.difference).toFixed(definition.decimals);
  const outcome = comparison.difference === 0 ? 'matches' : comparison.isFavorable ? 'better' : 'behind';
  return comparison.difference === 0
    ? 'Matches benchmark'
    : `${amount} ${definition.unit} ${outcome}`;
}
